import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import IndexPage from 'flarum/forum/components/IndexPage';
import NotificationGrid from 'flarum/forum/components/NotificationGrid';
import NotificationList from 'flarum/forum/components/NotificationList';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import Discussion from 'flarum/common/models/Discussion';
import Post from 'flarum/common/models/Post';
import Model from 'flarum/common/Model';
import Stream from 'flarum/common/utils/Stream';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import PostControls from 'flarum/forum/utils/PostControls';
import CommentPost from 'flarum/forum/components/CommentPost';

import EventBanner from './src/components/EventBanner';
import SectionSelector from './src/components/SectionSelector';
import EventsPage from './src/components/EventsPage';
import PendingValidationBanner from './src/components/PendingValidationBanner';
import AgogoWatchBanner from './src/components/AgogoWatchBanner';
import Event from './src/models/Event';
import Rsvp from './src/models/Rsvp';
import NewEventNotification from './src/components/NewEventNotification';
import EventStatusChangedNotification from './src/components/EventStatusChangedNotification';
import NewDiscussionInTagNotification from './src/components/NewDiscussionInTagNotification';
import NewPostInTagNotification from './src/components/NewPostInTagNotification';
import { initPushPermissionReporting, reportPushPermission } from './src/forum/push-permission';
import initAppBadge from './src/forum/app-badge';
app.initializers.add('florcarioca/flarum-rsvp', () => {
  // fof/pwa shows its own device opt-in alert (bottom corner) when a push
  // preference is enabled but this device isn't subscribed yet, so we mark it
  // as dismissed before it ever renders.
  try {
    window.localStorage.setItem('fof-pwa.notif-alert.dismissed', JSON.stringify({ timestamp: Date.now() }));
  } catch (e) {
    // ignore storage errors
  }

  initPushPermissionReporting();

  // Mirror the bell's unread count on the Home Screen app icon (iOS badge).
  initAppBadge();

  // Register models in the store
  app.store.models.events = Event;
  app.store.models.rsvps = Rsvp;

  // Register notification components
  app.notificationComponents.newEvent = NewEventNotification;
  app.notificationComponents.eventStatusChanged = EventStatusChangedNotification;

  // Add event relationship to Discussion model
  Discussion.prototype.event = Model.hasOne('event');

  // Register the events page route
  app.routes.events = {
    path: '/events',
    component: EventsPage,
  };

  // Add events link to navigation
  extend(IndexPage.prototype, 'navItems', function (items) {
    items.add(
      'events',
      <LinkButton href={app.route('events')} icon="fas fa-calendar-alt">
        {app.translator.trans('florcarioca-rsvp.forum.nav.events')}
      </LinkButton>,
      80
    );
  });

  // Replace the post scrubber in the mobile header with the site nav dropdown,
  // and show the discussion's primary tag name as the dropdown label.
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    items.remove('scrubber');

    const discussion = this.discussion;
    let label;
    if (discussion && discussion.attribute('isEvent')) {
      label = app.translator.trans('florcarioca-rsvp.forum.nav.events');
    } else {
      const tags = discussion && typeof discussion.tags === 'function' ? discussion.tags() : null;
      const primaryTag = tags && tags.length > 0 ? tags[0] : null;
      label = primaryTag ? primaryTag.name() : app.translator.trans('core.forum.header.back_to_index_tooltip');
    }

    const navItems = IndexPage.prototype.navItems.call({ currentTag: () => null });

    items.add(
      'navDropdown',
      <SelectDropdown
        buttonClassName="Button"
        className="App-titleControl"
        defaultLabel={label}
        accessibleToggleLabel={app.translator.trans('core.forum.index.toggle_sidenav_dropdown_accessible_label')}
      >
        {navItems.toArray()}
      </SelectDropdown>,
      -100
    );
  });

  // Add event banner to discussion page (after hero, before posts)
  extend(DiscussionPage.prototype, 'pageContent', function (items) {
    const discussion = this.discussion;
    if (discussion && discussion.attribute('isEvent')) {
      items.add(
        'eventBanner',
        <div className="container">
          <EventBanner discussion={discussion} />
        </div>,
        90  // hero is 100, main is 10, so 90 puts it between
      );
    }
  });

  // Add event indicator to discussion list
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const discussion = this.attrs.discussion;
    if (discussion.attribute('isEvent')) {
      const event = discussion.event();
      if (event) {
        items.add(
          'eventDate',
          <span className="DiscussionListItem-eventDate">
            <i className="fas fa-calendar-alt"></i>
            {' '}
            {dayjs(event.eventDate()).format('DD/MM/YYYY HH:mm')}
          </span>,
          -10
        );
      }
    }
  });

  // Agogo surveillance banner: shown for 30 days after any agogo selection attempt
  setTimeout(() => {
    if (document.cookie.split(';').some((c) => c.trim().startsWith('agogo_watched='))) {
      const bannerContainer = document.createElement('div');
      bannerContainer.id = 'agogo-watch-banner';
      document.body.prepend(bannerContainer);
      m.mount(bannerContainer, AgogoWatchBanner);
      document.body.classList.add('has-agogo-banner');

      requestAnimationFrame(() => {
        const bannerEl = bannerContainer.firstElementChild;
        const height = bannerEl ? bannerEl.offsetHeight : 40;
        document.body.style.setProperty('--agogo-banner-height', height + 'px');
      });
    }
  }, 0);

  // Add pending validation banner for suspended users awaiting approval
  // Deferred because app.session is set after initializers run in Flarum's boot sequence
  setTimeout(() => {
    if (app.session && app.session.user) {
      const suspendedUntil = app.session.user.attribute('suspendedUntil');
      const suspendReason = app.session.user.attribute('suspendReason');

      if (suspendedUntil && suspendReason && suspendReason.includes('attente de validation')) {
        const bannerContainer = document.createElement('div');
        bannerContainer.id = 'pending-validation-banner';
        document.body.appendChild(bannerContainer);

        m.mount(bannerContainer, PendingValidationBanner);

        document.body.classList.add('has-pending-banner');

        // Set CSS variable with actual banner height for responsive layout
        requestAnimationFrame(() => {
          const height = bannerContainer.offsetHeight;
          document.body.style.setProperty('--pending-banner-height', height + 'px');
        });
      }
    }
  }, 0);

  // ===== SIGN UP MODAL: Add profile fields =====
  extend(SignUpModal.prototype, 'oninit', function () {
    this.firstName = Stream('');
    this.lastName = Stream('');
    this.phone = Stream('');
    this.defaultSection = Stream('');
  });

  extend(SignUpModal.prototype, 'fields', function (items) {
    const sections = JSON.parse(app.forum.attribute('florCariocaSections') || '[]');

    items.add(
      'firstName',
      <div className="Form-group">
        <input
          className="FormControl"
          name="firstName"
          type="text"
          placeholder={app.translator.trans('florcarioca-rsvp.forum.signup.first_name_placeholder')}
          bidi={this.firstName}
          disabled={this.loading}
        />
      </div>,
      5
    );

    items.add(
      'lastName',
      <div className="Form-group">
        <input
          className="FormControl"
          name="lastName"
          type="text"
          placeholder={app.translator.trans('florcarioca-rsvp.forum.signup.last_name_placeholder')}
          bidi={this.lastName}
          disabled={this.loading}
        />
      </div>,
      4
    );

    items.add(
      'phone',
      <div className="Form-group">
        <input
          className="FormControl"
          name="phone"
          type="text"
          placeholder={app.translator.trans('florcarioca-rsvp.forum.signup.phone_placeholder')}
          bidi={this.phone}
          disabled={this.loading}
        />
      </div>,
      3
    );

    items.add(
      'defaultSection',
      <div className="Form-group">
        <SectionSelector
          value={this.defaultSection()}
          onchange={(value) => this.defaultSection(value)}
          sections={sections}
          disabled={this.loading}
        />
      </div>,
      2
    );
  });

  extend(SignUpModal.prototype, 'submitData', function (data) {
    data.firstName = this.firstName();
    data.lastName = this.lastName();
    data.phone = this.phone();
    data.defaultSection = this.defaultSection();
  });

  // ===== SETTINGS PAGE: Full profile section =====
  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    const user = app.session.user;
    const sections = JSON.parse(app.forum.attribute('florCariocaSections') || '[]');

    // Initialize streams if not already present
    if (!this._profileFirstName) {
      this._profileFirstName = Stream(user.attribute('firstName') || '');
      this._profileLastName = Stream(user.attribute('lastName') || '');
      this._profilePhone = Stream(user.attribute('phone') || '');
      this._profileSection = Stream(user.attribute('defaultSection') || '');
      this._profileSaving = false;
      this._profileDirty = false;
      this._testPushLoading = false;
      this._testPushStatus = null;
    }

    const markDirty = () => { this._profileDirty = true; };

    const saveProfile = () => {
      this._profileSaving = true;

      user.save({
        firstName: this._profileFirstName(),
        lastName: this._profileLastName(),
        phone: this._profilePhone(),
        defaultSection: this._profileSection(),
      }).then(() => {
        this._profileSaving = false;
        this._profileDirty = false;
        m.redraw();
      }).catch(() => {
        this._profileSaving = false;
        m.redraw();
      });
    };

    items.add(
      'profile',
      <div className="ProfileSection">
        <h3>
          <i className="fas fa-user"></i>
          {app.translator.trans('florcarioca-rsvp.forum.profile.section_title')}
        </h3>

        <div className="Form-group">
          <label>{app.translator.trans('florcarioca-rsvp.forum.profile.first_name')}</label>
          <input
            className="FormControl"
            type="text"
            placeholder={app.translator.trans('florcarioca-rsvp.forum.profile.first_name_placeholder')}
            value={this._profileFirstName()}
            oninput={(e) => { this._profileFirstName(e.target.value); this._profileDirty = true; }}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('florcarioca-rsvp.forum.profile.last_name')}</label>
          <input
            className="FormControl"
            type="text"
            placeholder={app.translator.trans('florcarioca-rsvp.forum.profile.last_name_placeholder')}
            value={this._profileLastName()}
            oninput={(e) => { this._profileLastName(e.target.value); this._profileDirty = true; }}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('florcarioca-rsvp.forum.profile.phone')}</label>
          <input
            className="FormControl"
            type="tel"
            placeholder={app.translator.trans('florcarioca-rsvp.forum.profile.phone_placeholder')}
            value={this._profilePhone()}
            oninput={(e) => { this._profilePhone(e.target.value); this._profileDirty = true; }}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('florcarioca-rsvp.forum.profile.section')}</label>
          <SectionSelector
            value={this._profileSection()}
            onchange={(value) => {
              this._profileSection(value);
              this._profileDirty = true;
            }}
            sections={sections}
          />
          <p className="helpText">
            {app.translator.trans('florcarioca-rsvp.forum.settings.default_section_help')}
          </p>
        </div>

        {Button.component(
          {
            className: 'Button Button--primary',
            loading: this._profileSaving,
            disabled: !this._profileDirty,
            onclick: saveProfile,
          },
          app.translator.trans('florcarioca-rsvp.forum.profile.save')
        )}
      </div>,
      100
    );

    // Remove the old standalone defaultSection item if it exists
    if (items.has('defaultSection')) {
      items.remove('defaultSection');
    }
  });

  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    if (!this._profileFirstName) return;

    const sendTestPush = () => {
      this._testPushLoading = true;
      this._testPushStatus = null;
      m.redraw();

      app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/test-push-notification',
      }).then(() => {
        this._testPushLoading = false;
        this._testPushStatus = 'success';
        m.redraw();
      }).catch(() => {
        this._testPushLoading = false;
        this._testPushStatus = 'error';
        m.redraw();
      });
    };

    items.add(
      'pushNotificationTest',
      <div className="PushTestSection">
        <h3>
          <i className="fas fa-bell"></i>
          {' '}
          {app.translator.trans('florcarioca-rsvp.forum.settings.test_push')}
        </h3>

        {(() => {
          const permission = app.session.user.attribute('pushPermission');
          const permissionAt = app.session.user.attribute('pushPermissionAt');

          const permissionMeta = {
            granted: { icon: 'fas fa-check-circle', className: 'PushPermissionBadge PushPermissionBadge--granted', label: 'florcarioca-rsvp.forum.settings.push_permission_granted' },
            denied: { icon: 'fas fa-ban', className: 'PushPermissionBadge PushPermissionBadge--denied', label: 'florcarioca-rsvp.forum.settings.push_permission_denied' },
            default: { icon: 'fas fa-question-circle', className: 'PushPermissionBadge PushPermissionBadge--default', label: 'florcarioca-rsvp.forum.settings.push_permission_default' },
            unsupported: { icon: 'fas fa-times-circle', className: 'PushPermissionBadge PushPermissionBadge--unsupported', label: 'florcarioca-rsvp.forum.settings.push_permission_unsupported' },
            null: { icon: 'fas fa-minus-circle', className: 'PushPermissionBadge PushPermissionBadge--unknown', label: 'florcarioca-rsvp.forum.settings.push_permission_unknown' },
          };
          const meta = permissionMeta[permission] || permissionMeta.null;

          return (
            <div className="PushPermissionStatus">
              <span className={meta.className}>
                <i className={meta.icon}></i>
                {' '}
                {app.translator.trans(meta.label)}
              </span>
              {permissionAt && (
                <span className="PushPermissionStatus-at helpText">
                  {app.translator.trans('florcarioca-rsvp.forum.settings.push_permission_last_report', {
                    time: dayjs.unix(permissionAt).format('LLL'),
                  })}
                </span>
              )}
              {permission === 'denied' && (
                <p className="helpText">
                  {app.translator.trans('florcarioca-rsvp.forum.settings.push_permission_denied_help')}
                </p>
              )}
              {Button.component(
                {
                  className: 'Button Button--link PushPermissionStatus-refresh',
                  icon: 'fas fa-sync-alt',
                  onclick: () => reportPushPermission(true),
                },
                app.translator.trans('florcarioca-rsvp.forum.settings.push_permission_refresh')
              )}
            </div>
          );
        })()}

        <p className="helpText">
          {app.translator.trans('florcarioca-rsvp.forum.settings.test_push_help')}
        </p>
        {Button.component(
          {
            className: 'Button',
            loading: this._testPushLoading,
            onclick: sendTestPush,
          },
          app.translator.trans('florcarioca-rsvp.forum.settings.test_push_button')
        )}
        {this._testPushStatus === 'success' && (
          <p className="helpText" style="color: #27ae60; margin-top: 8px;">
            {app.translator.trans('florcarioca-rsvp.forum.settings.test_push_success')}
          </p>
        )}
        {this._testPushStatus === 'error' && (
          <p className="helpText" style="color: #e74c3c; margin-top: 8px;">
            {app.translator.trans('florcarioca-rsvp.forum.settings.test_push_error')}
          </p>
        )}
        <p className="helpText" style="margin-top: 12px;">
          {app.translator.trans('florcarioca-rsvp.forum.settings.test_push_device_note')}
        </p>
        <p className="helpText" style="margin-top: 12px; font-weight: bold;">
          {app.translator.trans('florcarioca-rsvp.forum.settings.browser_recommendation')}
        </p>
      </div>,
      50
    );
  });

  const ourNotificationTypes = ['newEvent', 'eventStatusChanged', 'newDiscussionInTag', 'newPostInTag'];

  override(NotificationList.prototype, 'groupView', function (original, group) {
    if (group && group.notifications && group.notifications.every((n) => ourNotificationTypes.includes(n.attribute('contentType')))) {
      return group.notifications.map((notification, i) => {
        const item = this.notificationItem(notification, i);
        if (!item) return null;
        return (
          <div className="NotificationGroup">
            <ul className="NotificationGroup-content">{item}</ul>
          </div>
        );
      }).filter(Boolean);
    }
    return original(group);
  });

  extend(NotificationGrid.prototype, 'notificationTypes', function (items) {
    items.add('newEvent', {
      name: 'newEvent',
      icon: 'fas fa-calendar-plus',
      label: app.translator.trans('florcarioca-rsvp.forum.notifications.new_event_label'),
    });

    items.add('eventStatusChanged', {
      name: 'eventStatusChanged',
      icon: 'fas fa-calendar-check',
      label: app.translator.trans('florcarioca-rsvp.forum.notifications.event_status_changed_label'),
    });

    // The "new member awaiting approval" toggle is only relevant to admins
    // (only they receive the notification), so only show it in their settings.
    if (app.session.user && app.session.user.isAdmin()) {
      items.add('newUserPendingApproval', {
        name: 'newUserPendingApproval',
        icon: 'fas fa-user-plus',
        label: app.translator.trans('florcarioca-rsvp.forum.notifications.new_user_pending_approval_label'),
      });
    }
  });

  // ===== PIN POST: Add pin/unpin controls to post dropdown =====
  extend(PostControls, 'moderationControls', function (items, post) {
    const discussion = post.discussion();
    if (!discussion || !discussion.attribute('canPinPost')) return;

    const isPinned = post.attribute('isPinned');

    items.add(
      'pinPost',
      <Button
        icon="fas fa-thumbtack"
        onclick={() => {
          app.request({
            method: isPinned ? 'DELETE' : 'POST',
            url: app.forum.attribute('apiUrl') + '/florcarioca/posts/' + post.id() + '/pin',
          }).then(() => {
            post.pushAttributes({ isPinned: !isPinned });
            const pinnedIds = (discussion.attribute('pinnedPostIds') || []).map(String);
            const postId = String(post.id());
            if (isPinned) {
              discussion.pushAttributes({ pinnedPostIds: pinnedIds.filter((id) => id !== postId) });
            } else {
              discussion.pushAttributes({ pinnedPostIds: [...pinnedIds, postId] });
            }
            m.redraw();
          });
        }}
      >
        {app.translator.trans(
          isPinned
            ? 'florcarioca-rsvp.forum.pinned_posts.unpin'
            : 'florcarioca-rsvp.forum.pinned_posts.pin'
        )}
      </Button>,
      50
    );
  });

  // ===== PIN POST: Show pinned posts section at top of discussion =====
  extend(DiscussionPage.prototype, 'pageContent', function (items) {
    const discussion = this.discussion;
    if (!discussion) return;

    const pinnedPostIds = discussion.attribute('pinnedPostIds');
    if (!pinnedPostIds || !pinnedPostIds.length) return;

    const pinnedPosts = pinnedPostIds
      .map((id) => app.store.getById('posts', String(id)))
      .filter(Boolean);

    if (!pinnedPosts.length) return;

    items.add(
      'pinnedPosts',
      <div className="container">
        <div className="PinnedPosts">
          <h3 className="PinnedPosts-title">
            <i className="fas fa-thumbtack"></i>
            {' '}
            {app.translator.trans('florcarioca-rsvp.forum.pinned_posts.title')}
          </h3>
          {pinnedPosts.map((post) => {
            const user = post.user();
            return (
              <div className="PinnedPosts-post" key={post.id()}>
                <div className="PinnedPosts-post-content">
                  {user && <strong>{user.displayName()}</strong>}
                  <div innerHTML={post.contentHtml()} />
                </div>
                <a
                  className="Button Button--link PinnedPosts-jump"
                  onclick={(e) => {
                    e.preventDefault();
                    const el = document.querySelector('[data-id="' + post.id() + '"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  href={'#' + post.attribute('number')}
                >
                  #{post.attribute('number')}
                </a>
              </div>
            );
          })}
        </div>
      </div>,
      15
    );
  });
});

// Late initializer (priority -100) to override fof/follow-tags notification components.
// fof/follow-tags loads after us alphabetically and overwrites app.notificationComponents,
// so we must register ours after their initializer runs.
app.initializers.add('florcarioca/flarum-rsvp-notification-overrides', () => {
  app.notificationComponents.newDiscussionInTag = NewDiscussionInTagNotification;
  app.notificationComponents.newPostInTag = NewPostInTagNotification;
}, -100);

// Late initializer (priority -100) so that it runs after fof/rich-text's initializer
// regardless of extension load order. The FoF Rich Text composer preferences are
// forced on for everyone via the CurrentUserSerializer, so we hide the settings.
app.initializers.add('florcarioca/flarum-rsvp-richtext-settings', () => {
  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    items.remove('composer');
  });
}, -100);
